import { lazy } from 'react';

/**
 * lazyRetry — React.lazy that survives a flaky mobile connection.
 *
 * CON-1606. Patrick, in Vietnam on mobile, repeatedly:
 *   408 Request Timeout
 *   TypeError: Cannot use 'in' operator to search for 'default' in undefined
 *
 * The second error IS the first one's consequence: when a dynamic import()
 * fails mid-flight the module resolves to undefined, React looks for `default`
 * on it, and the whole route white-pages. Plain React.lazy has no retry — one
 * dropped packet on a 3G handover kills the screen until a manual reload.
 *
 * Measured 2026-08-09: staging backend answers in 0.4s and Fly is healthy, so
 * this is transport flakiness on the device side, not a server fault — which is
 * exactly the case the app has to tolerate rather than blame.
 *
 * Strategy: retry the import with a short backoff, then fall back to a one-time
 * cache-busting reload (the chunk hash may genuinely be gone after a deploy).
 * The sessionStorage guard prevents a reload loop when the device is simply
 * offline.
 */
export function lazyRetry(factory, name = 'chunk', retries = 3, delayMs = 400) {
  return lazy(() =>
    new Promise((resolve, reject) => {
      const attempt = (left, wait) => {
        factory()
          .then((mod) => {
            // 🔴 CON-1619 — Rollup may UNWRAP the dynamic import. When a chunk's
            // namespace is used for exactly one export it rewrites
            //   import('./MapPage-x.js')            →
            //   import('./MapPage-x.js').then(e => e.a)
            // so the factory hands back the COMPONENT ITSELF, not a module
            // namespace. The old check demanded typeof 'object' + a `default`
            // key, so a perfectly good component threw "empty module" and the
            // route white-screened. It is chunking-dependent, which is why it
            // appeared out of nowhere on v70.281 after an unrelated i18n change
            // shifted the graph — the module was never empty.
            //
            // Accept BOTH shapes and normalise to what React.lazy wants.
            const m = (typeof mod === 'function')
              ? { default: mod }                                   // Rollup unwrapped it
              : (mod && typeof mod === 'object' && 'default' in mod)
                ? mod                                              // normal namespace
                : null;                                            // genuinely empty
            if (!m) throw new Error(`empty module for ${name}`);
            resolve(m);
          })
          .catch((err) => {
            if (left > 0) {
              setTimeout(() => attempt(left - 1, wait * 2), wait);
              return;
            }
            // Out of retries — the chunk hash is probably stale after a deploy,
            // so clear the caches, drop the service worker and reload.
            //
            // 🔴 THE GUARD IS PER-CHUNK, NOT PER-SESSION (fixed 2026-08-10).
            // It used to be a single `hl_lazy_recovered` flag for the whole
            // session. On a day with several consumer deploys that flag is spent
            // by the first stale chunk, and EVERY later one skips the recovery
            // and white-pages instead — which is exactly what happened on
            // vn.honestlee.app after four deploys in one morning: the apex was
            // serving all chunks as 200 application/javascript while Patrick got
            // "empty module for MergeAccountsPrompt". The deployment was healthy;
            // the recovery had simply been disarmed by an earlier route.
            //
            // Per-chunk keys let each route heal itself once. A global timestamp
            // still prevents a reload LOOP, which is what the original single
            // flag was really protecting against — but it only needs to stop
            // reloads within a few seconds of each other, not for all time.
            const KEY = `hl_lazy_recovered:${name}`;
            const LAST = 'hl_lazy_reload_at';
            const last = Number(sessionStorage.getItem(LAST) || 0);
            const looping = last && Date.now() - last < 10_000;

            if (!sessionStorage.getItem(KEY) && !looping) {
              sessionStorage.setItem(KEY, '1');
              sessionStorage.setItem(LAST, String(Date.now()));
              const go = () => window.location.reload();
              Promise.all([
                caches?.keys?.().then((ks) => Promise.all(ks.map((k) => caches.delete(k)))).catch(() => {}),
                navigator.serviceWorker?.getRegistrations?.()
                  .then((rs) => Promise.all(rs.map((r) => r.unregister()))).catch(() => {}),
              ]).then(go, go);
              return;
            }
            reject(err);   // genuinely offline, or reloading in a loop — show the boundary
          });
      };
      attempt(retries, delayMs);
    }),
  );
}

export default lazyRetry;
